import { Box, Paper } from '@mui/material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useEffect, useState, useRef, useCallback } from 'react';
import CategoryBar from './components/CategoryBar/CategoryBar';
import { FoodTable } from './components/FoodTable/FoodTable';
import { useUserMenuContext } from '../../context/UserMenuContext';
import { UnsavedChangesBanner } from '../../components/UnsavedChangesBanner/UnsavedChangesBanner';
import { ICategoryDetails } from '../MenuManagement/menu.types';
import { MenuType } from '../UserMenus/userMenu.types';
import UserMenuSkeleton from './components/UserMenuSkeleton/UserMenuSkeleton';
import AppPreview from './components/AppPreview/AppPreview';

interface UserMenuProps {
  menuType?: MenuType;
}

export default function UserMenu({ menuType }: UserMenuProps) {
  const { setActiveMenuType, categories: contextCategories, updateCategoryOrder, loading } = useUserMenuContext();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localCategories, setLocalCategories] = useState<ICategoryDetails[]>([]);
  const initialCategoriesRef = useRef<ICategoryDetails[]>([]);
  const foodTableSaveRef = useRef<(() => Promise<void>) | null>(null);
  const foodTableCancelRef = useRef<(() => void) | null>(null);
  const foodTableHasChangesRef = useRef<boolean>(false);

  useEffect(() => {
    if (menuType) {
      setActiveMenuType(menuType);
    }
  }, [menuType, setActiveMenuType]);

  useEffect(() => {
    setLocalCategories(contextCategories);
    initialCategoriesRef.current = contextCategories;
  }, [contextCategories]);

  useEffect(() => {
    const categoriesChanged = JSON.stringify(localCategories) !== JSON.stringify(initialCategoriesRef.current);
    const foodsChanged = foodTableHasChangesRef.current;
    setHasUnsavedChanges(categoriesChanged || foodsChanged);
  }, [localCategories]);

  const handleCategoryOrderChange = useCallback((newCategories: ICategoryDetails[]) => {
    setLocalCategories(newCategories);
  }, []);

  const handleFoodTableChanges = useCallback(
    (hasChanges: boolean) => {
      foodTableHasChangesRef.current = hasChanges;
      const categoriesChanged = JSON.stringify(localCategories) !== JSON.stringify(initialCategoriesRef.current);
      setHasUnsavedChanges(categoriesChanged || hasChanges);
    },
    [localCategories]
  );

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const categoriesChanged = JSON.stringify(localCategories) !== JSON.stringify(initialCategoriesRef.current);

      if (categoriesChanged) {
        await updateCategoryOrder(localCategories);
        initialCategoriesRef.current = localCategories;
      }

      if (foodTableSaveRef.current) {
        await foodTableSaveRef.current();
      }

      setHasUnsavedChanges(false);
      foodTableHasChangesRef.current = false;
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalCategories(initialCategoriesRef.current);
    if (foodTableCancelRef.current) {
      foodTableCancelRef.current();
    }
    setHasUnsavedChanges(false);
    foodTableHasChangesRef.current = false;
  };

  if (loading) {
    return <UserMenuSkeleton />;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(700px, 2fr) minmax(380px, 1fr)',
        gap: 2,
        height: 'calc(100vh - 72px)',
        maxHeight: 'calc(100vh - 72px)',
        boxSizing: 'border-box',
        p: 2,
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        alignItems: 'start',
        position: 'relative',
      }}
    >
      <DndProvider backend={HTML5Backend}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: '100%',
            minHeight: 0,
            position: 'relative',
          }}
        >
          <Paper
            sx={{
              p: 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CategoryBar categories={localCategories} onCategoryOrderChange={handleCategoryOrderChange} />
          </Paper>

          <Paper
            sx={{
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              pb: hasUnsavedChanges ? '80px' : 0,
              transition: 'padding-bottom 0.2s ease-in-out',
            }}
          >
            <FoodTable onSaveRef={foodTableSaveRef} onCancelRef={foodTableCancelRef} onChangesDetected={handleFoodTableChanges} />
          </Paper>

          {hasUnsavedChanges && (
            <Box sx={{ position: 'absolute', width: '100%', bottom: 0, left: 0, zIndex: 10 }}>
              <UnsavedChangesBanner onSave={handleSave} onCancel={handleCancel} isSaving={isSaving} />
            </Box>
          )}
        </Box>
      </DndProvider>
      {/* Right Column - Preview */}
      <Paper
        sx={{
          p: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <AppPreview />
      </Paper>
    </Box>
  );
}
